/**
 * Comprehensive form validation utilities
 */

import {
  MAX_FILE_SIZE_BYTES,
  SUPPORTED_FORMATS
} from "@/config/file-upload-config";

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export interface EmailValidationOptions {
  required?: boolean;
  allowEmpty?: boolean;
}

export interface NameValidationOptions {
  minLength?: number;
  maxLength?: number;
  allowSpecialChars?: boolean;
}

export interface PhoneValidationOptions {
  required?: boolean;
  allowEmpty?: boolean;
  countryCode?: string;
}

/**
 * Validates email address format
 */
export function validateEmail(
  email: string,
  options: EmailValidationOptions = {}
): { isValid: boolean; error?: string } {
  const { required = true, allowEmpty = false } = options;

  // Check if empty
  if (!email.trim()) {
    if (required && !allowEmpty) {
      return { isValid: false, error: "Email is required" };
    }
    if (allowEmpty) {
      return { isValid: true };
    }
  }

  // Email format validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: "Please enter a valid email address" };
  }

  // Check for common disposable email domains
  const disposableDomains = [
    "tempmail.org",
    "temp-mail.org",
    "guerrillamail.com",
    "10minutemail.com",
    "mailinator.com",
    "yopmail.com",
    "throwaway.email",
    "temp-mail.io"
  ];

  const domain = email.split("@")[1]?.toLowerCase();
  if (domain && disposableDomains.includes(domain)) {
    return { isValid: false, error: "Please use a valid email address" };
  }

  return { isValid: true };
}

/**
 * Validates if an email is a valid user email (not a GUID or placeholder)
 * Used specifically for user profile display logic
 */
export function isValidUserEmail(email: string): boolean {
  if (!email) return false;
  if (email === "no-email@tempii.ai") return false;

  // Check if it's a GUID (simple regex for common GUID formats)
  const guidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (guidRegex.test(email)) return false;

  // Check if it looks like a valid email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Generates avatar fallback initials from a user's full name
 * Takes the first letter of each word and returns up to 2 characters
 */
export function getAvatarFallback(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Validates phone number format
 */
export function validatePhoneNumber(
  phone: string,
  options: PhoneValidationOptions = {}
): { isValid: boolean; error?: string } {
  const { required = true, allowEmpty = false, countryCode = "US" } = options;

  // Check if empty
  if (!phone.trim()) {
    if (required && !allowEmpty) {
      return { isValid: false, error: "Phone number is required" };
    }
    if (allowEmpty) {
      return { isValid: true };
    }
  }

  // Remove all non-digit characters for validation
  const cleanPhone = phone.replace(/\D/g, "");

  // Check if phone number is too short or too long
  if (cleanPhone.length < 10) {
    return { isValid: false, error: "Phone number must be at least 10 digits" };
  }

  if (cleanPhone.length > 15) {
    return { isValid: false, error: "Phone number cannot exceed 15 digits" };
  }

  // Common phone number patterns for different countries
  const phonePatterns = {
    US: /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
    CA: /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
    UK: /^(\+?44[-.\s]?)?\(?([0-9]{2,4})\)?[-.\s]?([0-9]{3,4})[-.\s]?([0-9]{4})$/,
    AU: /^(\+?61[-.\s]?)?\(?([0-9]{2})\)?[-.\s]?([0-9]{4})[-.\s]?([0-9]{4})$/,
    // Generic international pattern
    INTERNATIONAL: /^(\+?[1-9]\d{1,14})$/
  };

  // Test against the appropriate pattern
  const pattern =
    phonePatterns[countryCode as keyof typeof phonePatterns] ||
    phonePatterns.INTERNATIONAL;

  if (!pattern.test(phone)) {
    return { isValid: false, error: "Please enter a valid phone number" };
  }

  return { isValid: true };
}

/**
 * Formats phone number for display
 */
export function formatPhoneNumber(
  phone: string,
  countryCode: string = "US"
): string {
  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, "");

  // Don't format if too short
  if (cleanPhone.length < 10) {
    return phone;
  }

  // Format based on country
  switch (countryCode) {
    case "US":
    case "CA":
      if (cleanPhone.length === 10) {
        return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(
          3,
          6
        )}-${cleanPhone.slice(6)}`;
      } else if (cleanPhone.length === 11 && cleanPhone.startsWith("1")) {
        return `+1 (${cleanPhone.slice(1, 4)}) ${cleanPhone.slice(
          4,
          7
        )}-${cleanPhone.slice(7)}`;
      }
      break;
    case "UK":
      if (cleanPhone.length === 10) {
        return `${cleanPhone.slice(0, 4)} ${cleanPhone.slice(
          4,
          7
        )} ${cleanPhone.slice(7)}`;
      } else if (cleanPhone.length === 11 && cleanPhone.startsWith("44")) {
        return `+44 ${cleanPhone.slice(2, 6)} ${cleanPhone.slice(
          6,
          9
        )} ${cleanPhone.slice(9)}`;
      }
      break;
    case "AU":
      if (cleanPhone.length === 10) {
        return `${cleanPhone.slice(0, 4)} ${cleanPhone.slice(
          4,
          7
        )} ${cleanPhone.slice(7)}`;
      } else if (cleanPhone.length === 11 && cleanPhone.startsWith("61")) {
        return `+61 ${cleanPhone.slice(2, 6)} ${cleanPhone.slice(
          6,
          9
        )} ${cleanPhone.slice(9)}`;
      }
      break;
  }

  // Return original if no specific formatting applies
  return phone;
}

/**
 * Validates name fields
 */
export function validateName(
  name: string,
  fieldName: string,
  options: NameValidationOptions = {}
): { isValid: boolean; error?: string } {
  const { minLength = 1, maxLength = 50, allowSpecialChars = false } = options;

  // Check if empty
  if (!name.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  // Check minimum length
  if (name.trim().length < minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minLength} character(s) long`
    };
  }

  // Check maximum length
  if (name.length > maxLength) {
    return {
      isValid: false,
      error: `${fieldName} must be less than ${maxLength} characters`
    };
  }

  // Check for special characters
  if (!allowSpecialChars && /[!@#$%^&*(),.?":{}|<>]/.test(name)) {
    return {
      isValid: false,
      error: `${fieldName} cannot contain special characters`
    };
  }

  // Check for numbers (optional - you might want to allow names with numbers)
  if (!allowSpecialChars && /\d/.test(name)) {
    return { isValid: false, error: `${fieldName} cannot contain numbers` };
  }

  return { isValid: true };
}

/**
 * Validates terms acceptance
 */
export function validateTermsAcceptance(accepted: boolean): {
  isValid: boolean;
  error?: string;
} {
  if (!accepted) {
    return {
      isValid: false,
      error: "You must accept the terms and conditions"
    };
  }

  return { isValid: true };
}

/**
 * Comprehensive login form validation (email only for Entra ID)
 */
export function validateEmailLoginForm(formData: {
  email: string;
}): ValidationResult {
  const errors: Record<string, string> = {};

  // Email validation
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error!;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

// Validation for file upload

export const getSupportedExtensions = (): string[] =>
  Object.values(SUPPORTED_FORMATS).flatMap((ext) => ext.split(","));

export const isValidFileExtension = (extension: string): boolean =>
  getSupportedExtensions().includes(extension.toLowerCase());

export const isValidFileSize = (sizeInBytes: number): boolean =>
  sizeInBytes <= MAX_FILE_SIZE_BYTES;

export const isUploadFileValid = (file: File): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!isValidFileSize(file.size)) {
    errors.size = "Max file size exceeded";
  }

  if (!isValidFileExtension(file.type)) {
    errors.extension = "Invalid file extension";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
